import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import { focoStatusSlug } from '../utils/saude-foco';

function getApiUrl(): string {
  // 1) Override explicito (build mobile/Capacitor, deploys especiais)
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  // Resolve a API conforme como a pagina foi servida, para funcionar igual
  // em dev (por IP) e em producao (por dominio HTTPS), sem cravar URL por ambiente.
  // Tambem cobre o cenario de SSO em iframe (origem diferente de localhost).
  if (typeof window !== 'undefined') {
    const { protocol, hostname, port } = window.location;
    // 2) Atras de proxy reverso (porta padrao 80/443 = dominio): API na MESMA
    //    origem sob /api (o proxy encaminha /api -> backend).
    if (!port || port === '80' || port === '443') {
      return '/api';
    }
    // 3) Dev por IP:porta (ex.: :3000): fala direto com o backend na :3001.
    return `${protocol}//${hostname}:3001`;
  }
  return 'http://localhost:3001';
}

const API_URL = getApiUrl();

// Expoe a base da API resolvida para scripts injetados (ex.: codigo_html dos embeds de BI),
// que rodam fora do bundle e nao enxergam REACT_APP_API_URL.
if (typeof window !== 'undefined') {
  (window as any).__NEOGESTOR_API_BASE__ = API_URL;
}

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper seguro para localStorage (pode falhar em iframes de terceiros)
function safeGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

// Fonte dos indicadores (consolidado D-1 x tempo real). Lida do sessionStorage em vez de
// virar parametro das funcoes abaixo: `getEixoResumo` ja tem 5 posicionais e `getListaNominal`
// tem 11 — somar mais um em cada assinatura, e propagar por todas as telas, so espalharia ruido.
// O componente que altera o valor e o FonteDadosSelector (components/saude).
function fonteAtualDaSessao(): string {
  try {
    const raw = sessionStorage.getItem('saude:fonte');
    return raw === 'tempo_real' || raw === 'snapshot' ? raw : 'snapshot';
  } catch {
    return 'snapshot';
  }
}

// Prefixos cujas leituras carregam a fonte escolhida (consolidado D-1 x tempo real).
//
// `/saude/cvat` vai SEM barra final de proposito: a rota principal e a raiz do controller
// (`GET /saude/cvat`), e com a barra a tela mais cara do conjunto ficaria de fora.
// Exportacoes (CSV/PDF) passam pelo mesmo interceptor e herdam a fonte — e o que se quer: a
// planilha precisa concordar com a tela de onde saiu.
const PREFIXOS_COM_FONTE = [
  '/saude/indicadores/',
  '/saude/cvat',
  '/saude/qualidade-dados/',
  '/saude/dados-sociodemograficos/',
];

// Interceptor que anexa a fonte a essas leituras. Endpoints que ainda nao consomem o parametro
// simplesmente o ignoram, entao nao ha risco em aplica-lo ao namespace inteiro.
api.interceptors.request.use((config) => {
  const url = config.url || '';
  if ((config.method || 'get').toLowerCase() === 'get' && PREFIXOS_COM_FONTE.some((p) => url.startsWith(p))) {
    config.params = { ...(config.params || {}), fonte: fonteAtualDaSessao() };
  }
  return config;
});

// Interceptor para adicionar token
api.interceptors.request.use(
  (config) => {
    const token = safeGetItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para tratar erros (401 = sessão inválida, limpa e redireciona — exceto na tela de login)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const isPublicAuthRequest =
        error.config?.url?.includes('/auth/login') ||
        error.config?.url?.includes('/auth/sso') ||
        error.config?.url?.includes('/auth/forgot-password') ||
        error.config?.url?.includes('/auth/reset-password') ||
        error.config?.url?.includes('/auth/validate-reset-token');
      // Nao redirecionar se foi tentativa de login/reset com credenciais erradas
      if (!isPublicAuthRequest) {
        try {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('municipio_ativo');
        } catch { /* iframe context */ }
        if (Capacitor.isNativePlatform()) {
          // No app empacotado (Capacitor), navegar direto para '/login' resultaria em 404
          // no servidor local do WebView. Recarregamos o index ('/') e o PrivateRoute
          // (routes/index.tsx) redireciona para /login de forma client-side.
          window.location.href = '/';
        } else {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// Serviços de autenticação
export const authService = {
  login: async (email: string, password: string) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },
  
  ssoLogin: async (cpf: string, token: string) => {
    const response = await api.post('/auth/sso', { cpf, token });
    return response.data;
  },
};

// Serviços de contexto
export const contextService = {
  getContext: async (municipioId?: number) => {
    const params = municipioId ? { municipio_id: municipioId } : {};
    const response = await api.get('/context', { params });
    return response.data;
  },
};

// Serviços de menus
export const menusService = {
  getMenus: async (municipioId?: number) => {
    const params = municipioId ? { municipio_id: municipioId } : {};
    const response = await api.get('/menus', { params });
    return response.data;
  },

  createMenu: async (data: any) => {
    const response = await api.post('/menus', data);
    return response.data;
  },

  updateMenu: async (id: number, data: any) => {
    const response = await api.patch(`/menus/${id}`, data);
    return response.data;
  },

  deleteMenu: async (id: number) => {
    const response = await api.delete(`/menus/${id}`);
    return response.data;
  },

  createSubmenu: async (menuId: number, data: any) => {
    const response = await api.post(`/menus/${menuId}/submenus`, data);
    return response.data;
  },

  updateSubmenu: async (id: number, data: any) => {
    const response = await api.patch(`/submenus/${id}`, data);
    return response.data;
  },

  deleteSubmenu: async (id: number) => {
    const response = await api.delete(`/submenus/${id}`);
    return response.data;
  },

  getMunicipiosByMenu: async (menuId: number) => {
    const response = await api.get(`/menus/${menuId}/municipios`);
    return response.data;
  },

  associateMunicipiosToMenu: async (menuId: number, municipioIds: number[]) => {
    const response = await api.post(`/menus/${menuId}/municipios`, { municipio_ids: municipioIds });
    return response.data;
  },

  removeMunicipioFromMenu: async (menuId: number, municipioId: number) => {
    const response = await api.delete(`/menus/${menuId}/municipios/${municipioId}`);
    return response.data;
  },
};

// Serviços de dashboards
export const dashboardsService = {
  getDashboard: async (submenuId: number) => {
    const response = await api.get(`/dashboards/${submenuId}`);
    return response.data;
  },
};

// Serviços de favoritos
export const favoritosService = {
  getFavoritos: async () => {
    const response = await api.get('/favoritos');
    return response.data;
  },
  
  addFavorito: async (submenuId: number) => {
    const response = await api.post('/favoritos', { submenu_id: submenuId });
    return response.data;
  },
  
  removeFavorito: async (submenuId: number) => {
    const response = await api.delete(`/favoritos/${submenuId}`);
    return response.data;
  },
};

// Serviços de comentários
export const comentariosService = {
  getComentarios: async (submenuId: number) => {
    const response = await api.get(`/comentarios/${submenuId}`);
    return response.data;
  },
  
  createComentario: async (submenuId: number, texto: string) => {
    const response = await api.post('/comentarios', { submenu_id: submenuId, texto });
    return response.data;
  },
  
  updateComentario: async (id: number, data: { texto?: string; status?: string }) => {
    const response = await api.patch(`/comentarios/${id}`, data);
    return response.data;
  },
  
  deleteComentario: async (id: number) => {
    const response = await api.delete(`/comentarios/${id}`);
    return response.data;
  },
};

// Serviços de usuários
export const usersService = {
  getUsers: async (municipioId?: number) => {
    const params = municipioId ? { municipio_id: municipioId } : {};
    const response = await api.get('/usuarios', { params });
    return response.data;
  },
  
  getUser: async (id: number) => {
    const response = await api.get(`/usuarios/${id}`);
    return response.data;
  },
  
  createUser: async (data: any) => {
    const response = await api.post('/usuarios', data);
    return response.data;
  },
  
  updateUser: async (id: number, data: any) => {
    const response = await api.patch(`/usuarios/${id}`, data);
    return response.data;
  },
  
  deleteUser: async (id: number) => {
    const response = await api.delete(`/usuarios/${id}`);
    return response.data;
  },
};

// Serviços de municípios
export const municipiosService = {
  getMunicipios: async () => {
    const response = await api.get('/municipios');
    return response.data;
  },

  getMunicipiosByUser: async () => {
    const response = await api.get('/municipios/user');
    return response.data;
  },

  getMunicipio: async (id: number) => {
    const response = await api.get(`/municipios/${id}`);
    return response.data;
  },

  // Parametros por municipio: catalogo (`parametros`) + valores do municipio.
  getParametros: async (id: number) => {
    const response = await api.get(`/municipios/${id}/parametros`);
    return response.data;
  },

  salvarParametros: async (id: number, itens: { chave: string; valor: string | null }[]) => {
    const response = await api.put(`/municipios/${id}/parametros`, { itens });
    return response.data;
  },

  createMunicipio: async (data: any) => {
    const response = await api.post('/municipios', data);
    return response.data;
  },

  updateMunicipio: async (id: number, data: any) => {
    const response = await api.patch(`/municipios/${id}`, data);
    return response.data;
  },

  deleteMunicipio: async (id: number) => {
    const response = await api.delete(`/municipios/${id}`);
    return response.data;
  },
};

// Serviços de perfis
export const perfisService = {
  getPerfis: async (municipioId?: number) => {
    const params = municipioId ? { municipio_id: municipioId } : {};
    const response = await api.get('/perfis', { params });
    return response.data;
  },

  getPermissoes: async () => {
    const response = await api.get('/permissoes');
    return response.data;
  },

  createPerfil: async (data: any) => {
    const response = await api.post('/perfis', data);
    return response.data;
  },

  updatePerfil: async (id: number, data: any) => {
    const response = await api.patch(`/perfis/${id}`, data);
    return response.data;
  },

  deletePerfil: async (id: number) => {
    const response = await api.delete(`/perfis/${id}`);
    return response.data;
  },

  copyPerfis: async (data: { source_municipio_id: number | null; target_municipio_id: number }) => {
    const response = await api.post('/perfis/copiar', data);
    return response.data;
  },
};

// Serviços de IA
export interface ChatRequest {
  municipio_id: number;
  contexto: 'dashboard' | 'saude';
  dashboard_id?: number;
  indicadores?: string[];
  eixo?: string;
  quadrimestre?: string;
  pergunta: string;
  historico?: { role: 'user' | 'assistant'; content: string }[];
  conversation_id?: number;
  provider?: string;
  dados_contexto?: string;
  imagem_base64?: string;
}

export interface WebSearchRequest {
  municipio_id: number;
  contexto: 'dashboard' | 'saude';
  query: string;
  pergunta: string;
  historico?: { role: 'user' | 'assistant'; content: string }[];
  conversation_id?: number;
  provider?: string;
  dashboard_id?: number;
  eixo?: string;
  quadrimestre?: string;
  dados_contexto?: string;
  resposta_parcial?: string;
}

export const iaService = {
  chat: async (data: ChatRequest) => {
    const response = await api.post('/ia/chat', data);
    return response.data;
  },

  chatStream: (data: ChatRequest): { reader: Promise<ReadableStreamDefaultReader<Uint8Array> | null>; abort: AbortController } => {
    const controller = new AbortController();
    const token = localStorage.getItem('token');
    const baseURL = api.defaults.baseURL || '';

    const readerPromise = fetch(`${baseURL}/ia/chat/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
      signal: controller.signal,
    }).then(async (res) => {
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        let msg = `Erro ${res.status}`;
        try { const j = JSON.parse(body); msg = Array.isArray(j.message) ? j.message.join(', ') : (j.message || msg); } catch {}
        throw new Error(msg);
      }
      return res.body?.getReader() || null;
    });

    return { reader: readerPromise, abort: controller };
  },

  webSearchStream: (data: WebSearchRequest): { reader: Promise<ReadableStreamDefaultReader<Uint8Array> | null>; abort: AbortController } => {
    const controller = new AbortController();
    const token = localStorage.getItem('token');
    const baseURL = api.defaults.baseURL || '';

    const readerPromise = fetch(`${baseURL}/ia/web-search`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
      signal: controller.signal,
    }).then(async (res) => {
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        let msg = `Erro ${res.status}`;
        try { const j = JSON.parse(body); msg = Array.isArray(j.message) ? j.message.join(', ') : (j.message || msg); } catch {}
        throw new Error(msg);
      }
      return res.body?.getReader() || null;
    });

    return { reader: readerPromise, abort: controller };
  },

  getProviders: async () => {
    const response = await api.get('/ia/providers');
    return response.data as { providers: string[]; default: string };
  },

  getSuggestions: async (municipioId: number, contexto: string, eixo?: string) => {
    const params = new URLSearchParams({ municipio_id: String(municipioId), contexto });
    if (eixo) params.set('eixo', eixo);
    const response = await api.get(`/ia/suggestions?${params}`);
    return response.data as string[];
  },

  getConversations: async (municipioId: number) => {
    const response = await api.get(`/ia/conversations?municipio_id=${municipioId}`);
    return response.data;
  },

  // Legacy compatibility
  analisar: async (data: {
    municipio_id: number;
    dashboard_id?: number;
    submenu_id?: number;
    pergunta?: string;
    indicadores?: string[];
    historico?: string[];
  }) => {
    const response = await api.post('/ia/analisar', data);
    return response.data;
  },
};

// Serviços de saúde
export const saudeService = {
  // Indicadores
  getEixos: async () => {
    const response = await api.get('/saude/indicadores/eixos');
    return response.data;
  },

  getResumo: async (municipioId: number, quadrimestre?: string, unidade?: string) => {
    const params: any = { municipio_id: municipioId };
    if (quadrimestre) params.quadrimestre = quadrimestre;
    if (unidade) params.unidade = unidade;
    const response = await api.get('/saude/indicadores/resumo', { params });
    return response.data;
  },

  getResumoGeral: async (municipioId: number, quadrimestre?: string, unidade?: string) => {
    const params: any = { municipio_id: municipioId };
    if (quadrimestre) params.quadrimestre = quadrimestre;
    if (unidade) params.unidade = unidade;
    const response = await api.get('/saude/indicadores/resumo', { params });
    return response.data;
  },

  getEquipes: async (municipioId: number) => {
    const response = await api.get('/saude/indicadores/equipes', {
      params: { municipio_id: municipioId },
    });
    return response.data;
  },

  // Contagem rapida de equipes por tipo (eSF+eAP, eSB, eMulti, eAPP, eCR) para o hero do dashboard
  getEquipesTipos: async (municipioId: number, unidade?: string) => {
    const params: any = { municipio_id: municipioId };
    if (unidade) params.unidade = unidade;
    const response = await api.get('/saude/indicadores/equipes-tipos', { params });
    return response.data;
  },

  // Lista de unidades de saude do municipio (para o seletor "Unidade de Saude")
  getUnidades: async (municipioId: number) => {
    const response = await api.get('/saude/indicadores/unidades', {
      params: { municipio_id: municipioId },
    });
    return response.data;
  },

  // Paineis de Eficiencia (POC 16.4) — atendimentos realizados nas ultimas 24h
  getAtendimentos24h: async (municipioId: number, unidade?: string) => {
    const params: any = { municipio_id: municipioId };
    if (unidade) params.unidade = unidade;
    const response = await api.get('/saude/eficiencia/atendimentos-24h', { params });
    return response.data;
  },

  // Paineis de Eficiencia (POC 16.5) — filas e tempo de espera
  getFilasEspera: async (
    municipioId: number,
    opts: { unidade?: string; sala?: string; inicio?: string; fim?: string } = {},
  ) => {
    const params: any = { municipio_id: municipioId };
    if (opts.unidade) params.unidade = opts.unidade;
    if (opts.sala) params.sala = opts.sala;
    if (opts.inicio) params.inicio = opts.inicio;
    if (opts.fim) params.fim = opts.fim;
    const response = await api.get('/saude/eficiencia/filas-espera', { params });
    return response.data;
  },

  // Lookup de salas de uma unidade (para o filtro dos paineis Filas e Espera / Filas de Consultorios)
  getSalas: async (municipioId: number, unidade?: string) => {
    const params: any = { municipio_id: municipioId };
    if (unidade) params.unidade = unidade;
    const response = await api.get('/saude/eficiencia/salas', { params });
    return response.data;
  },

  // Paineis de Eficiencia (POC 16.6) — filas de consultorios do dia (tempo medio de espera)
  getFilasConsultorios: async (
    municipioId: number,
    opts: { unidade?: string; sala?: string } = {},
  ) => {
    const params: any = { municipio_id: municipioId };
    if (opts.unidade) params.unidade = opts.unidade;
    if (opts.sala) params.sala = opts.sala;
    const response = await api.get('/saude/eficiencia/filas-consultorios', { params });
    return response.data;
  },

  // Paineis de Eficiencia (POC 16.7) — pacientes em espera de um consultorio (tempo individual)
  getFilasConsultoriosPacientes: async (
    municipioId: number,
    opts: { profissional: string; unidade?: string; sala?: string },
  ) => {
    const params: any = { municipio_id: municipioId, profissional: opts.profissional };
    if (opts.unidade) params.unidade = opts.unidade;
    if (opts.sala) params.sala = opts.sala;
    const response = await api.get('/saude/eficiencia/filas-consultorios/pacientes', { params });
    return response.data;
  },

  // Paineis de Eficiencia (POC 16.8) — indice de prescricoes (medicamentos e exames) nos atendimentos
  getIndicePrescricoes: async (
    municipioId: number,
    opts: { unidade?: string; inicio?: string; fim?: string } = {},
  ) => {
    const params: any = { municipio_id: municipioId };
    if (opts.unidade) params.unidade = opts.unidade;
    if (opts.inicio) params.inicio = opts.inicio;
    if (opts.fim) params.fim = opts.fim;
    const response = await api.get('/saude/eficiencia/indice-prescricoes', { params });
    return response.data;
  },

  // Paineis de Eficiencia (POC 16.9) — ocupacao de leitos (estado atual + movimentacao no periodo)
  getOcupacaoLeitos: async (
    municipioId: number,
    opts: { unidade?: string; sala?: string; inicio?: string; fim?: string } = {},
  ) => {
    const params: any = { municipio_id: municipioId };
    if (opts.unidade) params.unidade = opts.unidade;
    if (opts.sala) params.sala = opts.sala;
    if (opts.inicio) params.inicio = opts.inicio;
    if (opts.fim) params.fim = opts.fim;
    const response = await api.get('/saude/eficiencia/ocupacao-leitos', { params });
    return response.data;
  },

  // Paineis de Eficiencia (POC 16.10 / 16.11) — ao clicar numa sala: tempo medio de
  // permanencia + lista anonimizada dos pacientes internados (sexo, idade, tempo individual)
  getOcupacaoLeitosPacientes: async (
    municipioId: number,
    opts: { acomodacaoId: number | string },
  ) => {
    const params: any = { municipio_id: municipioId, acomodacao_id: opts.acomodacaoId };
    const response = await api.get('/saude/eficiencia/ocupacao-leitos/sala/pacientes', { params });
    return response.data;
  },

  // Paineis de Eficiencia (POC 16.16) — ao clicar num paciente internado: linha do tempo
  // do atendimento (admissao, evolucoes/anamnese com sinais vitais, alta). Anonimizado.
  getOcupacaoLeitosHistorico: async (
    municipioId: number,
    opts: { internacaoId: number | string },
  ) => {
    const params: any = { municipio_id: municipioId, internacao_id: opts.internacaoId };
    const response = await api.get('/saude/eficiencia/ocupacao-leitos/paciente/historico', { params });
    return response.data;
  },

  // Paineis de Eficiencia (POC 16.12 / 16.13) — produtividade por profissional no
  // turno de trabalho atual e anterior (total de atendimentos, tempo medio, % de prescricoes)
  getProdutividade: async (municipioId: number, opts: { unidade?: string } = {}) => {
    const params: any = { municipio_id: municipioId };
    if (opts.unidade) params.unidade = opts.unidade;
    const response = await api.get('/saude/eficiencia/produtividade', { params });
    return response.data;
  },

  // Paineis de Eficiencia (POC 16.15) — resultado da pesquisa de satisfacao com
  // pacientes/acompanhantes, por unidade e consolidado. So totais/agregacoes.
  // Periodo opcional [inicio, fim] (YYYY-MM-DD); ausente = tudo.
  getPesquisaSatisfacao: async (
    municipioId: number,
    opts: { unidade?: string; inicio?: string; fim?: string } = {},
  ) => {
    const params: any = { municipio_id: municipioId };
    if (opts.unidade) params.unidade = opts.unidade;
    if (opts.inicio) params.inicio = opts.inicio;
    if (opts.fim) params.fim = opts.fim;
    const response = await api.get('/saude/eficiencia/pesquisa-satisfacao', { params });
    return response.data;
  },

  // Paineis de Eficiencia (POC 16.14) — primeiras consultas e retornos, tempo medio
  // de atendimento e percentual de prescricoes, no periodo. So totais/agregacoes.
  // Periodo opcional [inicio, fim] (YYYY-MM-DD); ausente = ultimos 30 dias.
  getPrimeirosAtendimentos: async (
    municipioId: number,
    opts: { unidade?: string; inicio?: string; fim?: string } = {},
  ) => {
    const params: any = { municipio_id: municipioId };
    if (opts.unidade) params.unidade = opts.unidade;
    if (opts.inicio) params.inicio = opts.inicio;
    if (opts.fim) params.fim = opts.fim;
    const response = await api.get('/saude/eficiencia/primeiros-atendimentos', { params });
    return response.data;
  },

  // Paineis de Eficiencia (POC 16.17) — producao ambulatorial do periodo agregada
  // por procedimento (quantidade e valores) e por unidade. So totais/agregacoes.
  // Periodo opcional [inicio, fim] (YYYY-MM-DD); ausente = mes corrente.
  getProducaoAmbulatorial: async (
    municipioId: number,
    opts: { unidade?: string; inicio?: string; fim?: string } = {},
  ) => {
    const params: any = { municipio_id: municipioId };
    if (opts.unidade) params.unidade = opts.unidade;
    if (opts.inicio) params.inicio = opts.inicio;
    if (opts.fim) params.fim = opts.fim;
    const response = await api.get('/saude/eficiencia/producao-ambulatorial', { params });
    return response.data;
  },

  // Paineis de Eficiencia (POC) — estatisticas de internacao do periodo: tempo medio
  // de permanencia, pacientes-dia, leitos-dia, taxas de ocupacao hospitalar/operacional
  // e taxa de mortalidade. Quebrado por profissional e por unidade. So totais/agregacoes.
  // Periodo opcional [inicio, fim] (YYYY-MM-DD); ausente = mes corrente.
  getEstatisticasInternacao: async (
    municipioId: number,
    opts: { unidade?: string; inicio?: string; fim?: string } = {},
  ) => {
    const params: any = { municipio_id: municipioId };
    if (opts.unidade) params.unidade = opts.unidade;
    if (opts.inicio) params.inicio = opts.inicio;
    if (opts.fim) params.fim = opts.fim;
    const response = await api.get('/saude/eficiencia/estatisticas-internacao', { params });
    return response.data;
  },

  // Paineis de Eficiencia (POC 16.19 / 16.20) — tempo medio de regulacao das
  // solicitacoes e se esta na meta, por tipo de servico. So totais/agregacoes.
  // Periodo opcional [inicio, fim] (YYYY-MM-DD); ausente = ano corrente.
  getTempoRegulacao: async (
    municipioId: number,
    opts: { unidade?: string; inicio?: string; fim?: string } = {},
  ) => {
    const params: any = { municipio_id: municipioId };
    if (opts.unidade) params.unidade = opts.unidade;
    if (opts.inicio) params.inicio = opts.inicio;
    if (opts.fim) params.fim = opts.fim;
    const response = await api.get('/saude/eficiencia/tempo-regulacao', { params });
    return response.data;
  },

  // POC 16.20 — ao clicar num tipo de servico: tempo medio de regulacao do servico +
  // status da meta, detalhado por profissional regulador.
  getTempoRegulacaoServico: async (
    municipioId: number,
    opts: { servicoTipoId: number | string; unidade?: string; inicio?: string; fim?: string },
  ) => {
    const params: any = { municipio_id: municipioId, servico_tipo_id: opts.servicoTipoId };
    if (opts.unidade) params.unidade = opts.unidade;
    if (opts.inicio) params.inicio = opts.inicio;
    if (opts.fim) params.fim = opts.fim;
    const response = await api.get('/saude/eficiencia/tempo-regulacao/servico', { params });
    return response.data;
  },

  getEixoResumo: async (eixo: string, municipioId: number, ine?: string, quadrimestre?: string, unidade?: string) => {
    const params: any = { municipio_id: municipioId };
    if (ine) params.ine = ine;
    if (quadrimestre) params.quadrimestre = quadrimestre;
    if (unidade) params.unidade = unidade;
    const response = await api.get(`/saude/indicadores/${eixo}`, { params });
    return response.data;
  },

  getEixoEquipes: async (eixo: string, municipioId: number, quadrimestre?: string, unidade?: string) => {
    const params: any = { municipio_id: municipioId };
    if (quadrimestre) params.quadrimestre = quadrimestre;
    if (unidade) params.unidade = unidade;
    const response = await api.get(`/saude/indicadores/${eixo}/equipes`, { params });
    return response.data;
  },

  getListaNominal: async (
    eixo: string,
    municipioId: number,
    page: number = 1,
    limit: number = 50,
    ine?: string,
    quadrimestre?: string,
    unidade?: string,
    indicador?: string,
    status?: string,
    micro?: string,
    busca?: string,
  ) => {
    const params: any = { municipio_id: municipioId, page, limit };
    if (ine) params.ine = ine;
    if (quadrimestre) params.quadrimestre = quadrimestre;
    if (unidade) params.unidade = unidade;
    if (indicador) params.indicador = indicador;
    if (status) params.status = status;
    if (micro) params.micro = micro;
    if (busca) params.busca = busca;
    const response = await api.get(`/saude/indicadores/${eixo}/lista-nominal`, { params });
    return response.data;
  },

  exportarCsv: async (eixo: string, municipioId: number, ine?: string, quadrimestre?: string, unidade?: string, indicador?: string, status?: string, micro?: string, busca?: string) => {
    const params: any = { municipio_id: municipioId };
    if (ine) params.ine = ine;
    if (quadrimestre) params.quadrimestre = quadrimestre;
    if (unidade) params.unidade = unidade;
    if (indicador) params.indicador = indicador;
    if (status) params.status = status;
    if (micro) params.micro = micro;
    if (busca) params.busca = busca;
    const response = await api.get(`/saude/indicadores/${eixo}/exportar-csv`, {
      params,
      responseType: 'blob',
    });
    const sufixo = indicador ? `-${indicador.toUpperCase()}-${focoStatusSlug(eixo, status)}` : '';
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `lista-nominal-${eixo}${sufixo}-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  exportarBoaPraticaPdf: async (eixo: string, municipioId: number, indicador: string, status: string, quadrimestre?: string, unidade?: string, micro?: string, busca?: string) => {
    const params: any = { municipio_id: municipioId, indicador, status };
    if (quadrimestre) params.quadrimestre = quadrimestre;
    if (unidade) params.unidade = unidade;
    if (micro) params.micro = micro;
    if (busca) params.busca = busca;
    const response = await api.get(`/saude/indicadores/${eixo}/boa-pratica/relatorio-pdf`, {
      params,
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `boa-pratica-${eixo}-${indicador.toUpperCase()}-${focoStatusSlug(eixo, status)}-${new Date().toISOString().slice(0, 10)}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
  reloadDados: async (municipioId: number) => {
    // Corpo vazio como objeto ({}), nao null: axios serializa `null` como a string
    // "null" com Content-Type application/json, e o body-parser (strict) rejeita
    // com 400 "Unexpected token 'n', \"null\" is not valid JSON".
    const response = await api.post('/saude/indicadores/reload', {}, {
      params: { municipio_id: municipioId },
    });
    return response.data; // { deleted }
  },


  getEvolucaoTemporal: async (municipioId: number, unidade?: string) => {
    const params: any = { municipio_id: municipioId };
    if (unidade) params.unidade = unidade;
    const response = await api.get('/saude/indicadores/evolucao', { params });
    return response.data;
  },

  getRankingEquipes: async (municipioId: number, quadrimestre?: string, unidade?: string) => {
    const params: any = { municipio_id: municipioId };
    if (quadrimestre) params.quadrimestre = quadrimestre;
    if (unidade) params.unidade = unidade;
    const response = await api.get('/saude/indicadores/ranking-equipes', { params });
    return response.data;
  },

  getSimulacaoFinanceira: async (municipioId: number, quadrimestre?: string, unidade?: string) => {
    const params: any = { municipio_id: municipioId };
    if (quadrimestre) params.quadrimestre = quadrimestre;
    if (unidade) params.unidade = unidade;
    const response = await api.get('/saude/indicadores/simulacao-financeira', { params });
    return response.data;
  },

  getCompletude: async (municipioId: number, quadrimestre?: string, unidade?: string) => {
    const params: any = { municipio_id: municipioId };
    if (quadrimestre) params.quadrimestre = quadrimestre;
    if (unidade) params.unidade = unidade;
    const response = await api.get('/saude/indicadores/completude', { params });
    return response.data;
  },

  exportarPdf: async (municipioId: number, quadrimestre?: string, unidade?: string) => {
    const params: any = { municipio_id: municipioId };
    if (quadrimestre) params.quadrimestre = quadrimestre;
    if (unidade) params.unidade = unidade;
    const response = await api.get('/saude/indicadores/relatorio-pdf', {
      params,
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `relatorio-aps-${new Date().toISOString().slice(0, 10)}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  // Cobertura Vacinal oficial (painel do MS, por residencia)
  getCoberturaVacinal: async (municipioId: number, ano?: number) => {
    const params: any = { municipio_id: municipioId };
    if (ano) params.ano = ano;
    const response = await api.get('/saude/cobertura-vacinal', { params });
    return response.data;
  },
  getCoberturaVacinalLink: async (municipioId: number) => {
    const response = await api.get('/saude/cobertura-vacinal/link', { params: { municipio_id: municipioId } });
    return response.data;
  },
  gerarCoberturaVacinalLink: async (municipioId: number) => {
    const response = await api.post('/saude/cobertura-vacinal/link', { municipio_id: municipioId });
    return response.data;
  },
  atualizarCoberturaVacinal: async (municipioId: number) => {
    const response = await api.post('/saude/cobertura-vacinal/atualizar', { municipio_id: municipioId });
    return response.data;
  },
  /** Modo "Somente PEC": numeradores calculados com a regra do MS sobre as doses do PEC. */
  getCoberturaVacinalPec: async (municipioId: number, ano?: number) => {
    const params: any = { municipio_id: municipioId };
    if (ano) params.ano = ano;
    const response = await api.get('/saude/cobertura-vacinal/pec', { params });
    return response.data;
  },
  recalcularCoberturaVacinalPec: async (municipioId: number) => {
    const response = await api.post('/saude/cobertura-vacinal/pec/recalcular', { municipio_id: municipioId });
    return response.data;
  },
  /** PDF da tela na fonte escolhida ('ms' oficial ou 'pec'); download autenticado, como os demais relatorios. */
  exportarCoberturaVacinalPdf: async (municipioId: number, ano: number, fonte: 'ms' | 'pec') => {
    const response = await api.get('/saude/cobertura-vacinal/pdf', {
      params: { municipio_id: municipioId, ano, fonte },
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `cobertura-vacinal-${ano}-${fonte === 'pec' ? 'pec' : 'oficial'}-${new Date().toISOString().slice(0, 10)}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
  /** URL absoluta do PDF publico (a base da API pode ser relativa, ex.: '/api' atras do proxy). */
  urlPublicaCoberturaVacinal: (token: string): string => {
    const base = api.defaults.baseURL || '';
    const absoluta = /^https?:\/\//i.test(base) ? base : `${window.location.origin}${base}`;
    return `${absoluta.replace(/\/$/, '')}/publico/cobertura-vacinal/${token}.pdf`;
  },

  // Painel MS (RelatorioAPS)
  getResumoMS: async (municipioId: number) => {
    const response = await api.get('/saude/relatorio-aps/resumo', { params: { municipio_id: municipioId } });
    return response.data;
  },
  getPagamentoMS: async (municipioId: number, parcela?: string) => {
    const params: any = { municipio_id: municipioId };
    if (parcela) params.parcela = parcela;
    const response = await api.get('/saude/relatorio-aps/pagamento', { params });
    return response.data;
  },
  getSuspensaoMS: async (municipioId: number) => {
    const response = await api.get('/saude/relatorio-aps/suspensao', { params: { municipio_id: municipioId } });
    return response.data;
  },
  getCoberturaApsMS: async (municipioId: number) => {
    const response = await api.get('/saude/relatorio-aps/cobertura-aps', { params: { municipio_id: municipioId } });
    return response.data;
  },
  getCoberturaSbMS: async (municipioId: number) => {
    const response = await api.get('/saude/relatorio-aps/cobertura-sb', { params: { municipio_id: municipioId } });
    return response.data;
  },
  getEquipesHomologadasMS: async (municipioId: number) => {
    const response = await api.get('/saude/relatorio-aps/equipes-homologadas', { params: { municipio_id: municipioId } });
    return response.data;
  },

  // Relatórios — Agenda do dia x boas práticas pendentes
  // `unidade` = codunidade (co_seq_dim_unidade_saude) filtra a lista nominal;
  // `unidadeCnes` = codcnes filtra a agenda transacional. A página passa os dois.
  getAgendaPendencias: async (
    municipioId: number,
    data: string,
    unidade?: string,
    unidadeCnes?: string,
    profissional?: string,
    coLotacao?: string,
  ) => {
    const params: any = { municipio_id: municipioId, data };
    if (unidade) params.unidade = unidade;
    if (unidadeCnes) params.unidade_cnes = unidadeCnes;
    if (profissional) params.profissional = profissional;
    if (coLotacao) params.co_lotacao = coLotacao;
    const response = await api.get('/saude/relatorios/agenda-pendencias', { params });
    return response.data;
  },

  // Export da Agenda do dia (CSV e PDF) — reflete os filtros da tela (profissional + toggle).
  exportarAgendaCsv: async (
    municipioId: number, data: string, unidade?: string, unidadeCnes?: string,
    profissional?: string, somenteAcompanhamento?: boolean,
  ) => {
    const params: any = { municipio_id: municipioId, data };
    if (unidade) params.unidade = unidade;
    if (unidadeCnes) params.unidade_cnes = unidadeCnes;
    if (profissional) params.profissional = profissional;
    if (somenteAcompanhamento) params.somente_acompanhamento = 'true';
    const response = await api.get('/saude/relatorios/agenda-pendencias/exportar-csv', { params, responseType: 'blob' });
    if (!response.data || response.status === 204) return;
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `agenda-dia-${data}-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  exportarAgendaPdf: async (
    municipioId: number, data: string, unidade?: string, unidadeCnes?: string,
    profissional?: string, somenteAcompanhamento?: boolean,
  ) => {
    const params: any = { municipio_id: municipioId, data };
    if (unidade) params.unidade = unidade;
    if (unidadeCnes) params.unidade_cnes = unidadeCnes;
    if (profissional) params.profissional = profissional;
    if (somenteAcompanhamento) params.somente_acompanhamento = 'true';
    const response = await api.get('/saude/relatorios/agenda-pendencias/relatorio-pdf', { params, responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `agenda-dia-${data}-${new Date().toISOString().slice(0, 10)}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  // Relatórios — Resumo Executivo do Quadrimestre (briefing do gestor)
  getResumoExecutivo: async (municipioId: number, quadrimestre?: string, unidade?: string) => {
    const params: any = { municipio_id: municipioId };
    if (quadrimestre) params.quadrimestre = quadrimestre;
    if (unidade) params.unidade = unidade;
    const response = await api.get('/saude/relatorios/resumo-executivo', { params });
    return response.data;
  },

  exportarResumoExecutivoPdf: async (municipioId: number, quadrimestre?: string, unidade?: string) => {
    const params: any = { municipio_id: municipioId };
    if (quadrimestre) params.quadrimestre = quadrimestre;
    if (unidade) params.unidade = unidade;
    const response = await api.get('/saude/relatorios/resumo-executivo/relatorio-pdf', { params, responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `resumo-executivo-${quadrimestre || 'atual'}-${new Date().toISOString().slice(0, 10)}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  // Relatórios — Ritmo de Visitas do ACS (por microárea: gap de visitas, dias úteis e ritmo/dia)
  // ---- CVAT - Componente Vinculo e Acompanhamento Territorial (NT 30/2025) ----
  getCvat: async (municipioId: number, opts: { quadrimestre?: string; unidade?: string } = {}) => {
    const params: any = { municipio_id: municipioId };
    if (opts.quadrimestre) params.quadrimestre = opts.quadrimestre;
    if (opts.unidade) params.unidade = opts.unidade;
    const response = await api.get('/saude/cvat', { params });
    return response.data;
  },

  getCvatSaneamento: async (municipioId: number, opts: { quadrimestre?: string; unidade?: string; dias?: number } = {}) => {
    const params: any = { municipio_id: municipioId };
    if (opts.quadrimestre) params.quadrimestre = opts.quadrimestre;
    if (opts.unidade) params.unidade = opts.unidade;
    if (opts.dias) params.dias = opts.dias;
    const response = await api.get('/saude/cvat/saneamento', { params });
    return response.data;
  },

  getCvatSaneamentoLista: async (
    municipioId: number,
    tipo: string,
    opts: { quadrimestre?: string; unidade?: string; ine?: string; micro?: string; busca?: string; dias?: number; page?: number; limit?: number } = {},
  ) => {
    const params: any = { municipio_id: municipioId, page: opts.page || 1, limit: opts.limit || 50 };
    if (opts.quadrimestre) params.quadrimestre = opts.quadrimestre;
    if (opts.unidade) params.unidade = opts.unidade;
    if (opts.ine) params.ine = opts.ine;
    if (opts.micro) params.micro = opts.micro;
    if (opts.busca) params.busca = opts.busca;
    if (opts.dias) params.dias = opts.dias;
    const response = await api.get(`/saude/cvat/saneamento/${tipo}`, { params });
    return response.data;
  },

  exportarCvatSaneamentoCsv: async (
    municipioId: number,
    tipo: string,
    opts: { quadrimestre?: string; unidade?: string; ine?: string; micro?: string; busca?: string; dias?: number } = {},
  ) => {
    const params: any = { municipio_id: municipioId };
    if (opts.quadrimestre) params.quadrimestre = opts.quadrimestre;
    if (opts.unidade) params.unidade = opts.unidade;
    if (opts.ine) params.ine = opts.ine;
    if (opts.micro) params.micro = opts.micro;
    if (opts.busca) params.busca = opts.busca;
    if (opts.dias) params.dias = opts.dias;
    const response = await api.get(`/saude/cvat/saneamento/${tipo}/exportar-csv`, { params, responseType: 'blob' });
    if (!response.data || response.status === 204) return;
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `cvat-${tipo}-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  exportarCvatSaneamentoPdf: async (
    municipioId: number,
    tipo: string,
    opts: { quadrimestre?: string; unidade?: string; ine?: string; micro?: string; busca?: string; dias?: number } = {},
  ) => {
    const params: any = { municipio_id: municipioId };
    if (opts.quadrimestre) params.quadrimestre = opts.quadrimestre;
    if (opts.unidade) params.unidade = opts.unidade;
    if (opts.ine) params.ine = opts.ine;
    if (opts.micro) params.micro = opts.micro;
    if (opts.busca) params.busca = opts.busca;
    if (opts.dias) params.dias = opts.dias;
    const response = await api.get(`/saude/cvat/saneamento/${tipo}/relatorio-pdf`, { params, responseType: 'blob' });
    if (!response.data || response.status === 204) return;
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `cvat-${tipo}-${new Date().toISOString().slice(0, 10)}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  getRitmoVisitasAcs: async (municipioId: number, quadrimestre?: string, unidade?: string) => {
    const params: any = { municipio_id: municipioId };
    if (quadrimestre) params.quadrimestre = quadrimestre;
    if (unidade) params.unidade = unidade;
    const response = await api.get('/saude/relatorios/ritmo-visitas-acs', { params });
    return response.data;
  },

  exportarRitmoAcsCsv: async (municipioId: number, quadrimestre?: string, unidade?: string) => {
    const params: any = { municipio_id: municipioId };
    if (quadrimestre) params.quadrimestre = quadrimestre;
    if (unidade) params.unidade = unidade;
    const response = await api.get('/saude/relatorios/ritmo-visitas-acs/exportar-csv', { params, responseType: 'blob' });
    if (!response.data || response.status === 204) return;
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `ritmo-visitas-acs-${quadrimestre || 'atual'}-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  exportarRitmoAcsPdf: async (municipioId: number, quadrimestre?: string, unidade?: string) => {
    const params: any = { municipio_id: municipioId };
    if (quadrimestre) params.quadrimestre = quadrimestre;
    if (unidade) params.unidade = unidade;
    const response = await api.get('/saude/relatorios/ritmo-visitas-acs/relatorio-pdf', { params, responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `ritmo-visitas-acs-${quadrimestre || 'atual'}-${new Date().toISOString().slice(0, 10)}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  // Relatorio "Equipes sem Producao" (equipe zerada na competencia MM/AAAA)
  getEquipesSemProducao: async (
    municipioId: number,
    competencia?: string,
    unidade?: string,
    tipos?: string,
  ) => {
    const params: any = { municipio_id: municipioId };
    if (competencia) params.competencia = competencia;
    if (unidade) params.unidade = unidade;
    if (tipos) params.tipos = tipos;
    const response = await api.get('/saude/relatorios/equipes-sem-producao', { params });
    return response.data;
  },

  exportarEquipesSemProducaoCsv: async (
    municipioId: number,
    competencia?: string,
    unidade?: string,
    tipos?: string,
  ) => {
    const params: any = { municipio_id: municipioId };
    if (competencia) params.competencia = competencia;
    if (unidade) params.unidade = unidade;
    if (tipos) params.tipos = tipos;
    const response = await api.get('/saude/relatorios/equipes-sem-producao/exportar-csv', {
      params,
      responseType: 'blob',
    });
    if (!response.data || response.status === 204) return;
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `equipes-sem-producao-${competencia || 'atual'}-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  exportarEquipesSemProducaoPdf: async (
    municipioId: number,
    competencia?: string,
    unidade?: string,
    tipos?: string,
  ) => {
    const params: any = { municipio_id: municipioId };
    if (competencia) params.competencia = competencia;
    if (unidade) params.unidade = unidade;
    if (tipos) params.tipos = tipos;
    const response = await api.get('/saude/relatorios/equipes-sem-producao/relatorio-pdf', {
      params,
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `equipes-sem-producao-${competencia || 'atual'}-${new Date().toISOString().slice(0, 10)}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  // Relatorio "Fichas em Rascunho" (fichas digitadas no PEC e nunca finalizadas)
  getFichasRascunho: async (
    municipioId: number,
    competencia?: string,
    tipo?: string,
    unidade?: string,
  ) => {
    const params: any = { municipio_id: municipioId };
    if (competencia) params.competencia = competencia;
    if (tipo) params.tipo = tipo;
    if (unidade) params.unidade = unidade;
    const response = await api.get('/saude/relatorios/fichas-rascunho', { params });
    return response.data;
  },

  getFichasRascunhoCompetencias: async (municipioId: number, unidade?: string) => {
    const params: any = { municipio_id: municipioId };
    if (unidade) params.unidade = unidade;
    const response = await api.get('/saude/relatorios/fichas-rascunho/competencias', { params });
    return response.data;
  },

  exportarFichasRascunhoCsv: async (
    municipioId: number,
    competencia?: string,
    tipo?: string,
    unidade?: string,
  ) => {
    const params: any = { municipio_id: municipioId };
    if (competencia) params.competencia = competencia;
    if (tipo) params.tipo = tipo;
    if (unidade) params.unidade = unidade;
    const response = await api.get('/saude/relatorios/fichas-rascunho/exportar-csv', {
      params,
      responseType: 'blob',
    });
    if (!response.data || response.status === 204) return;
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `fichas-rascunho-${competencia || 'atual'}-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  gerarFichasRascunhoPdf: async (
    municipioId: number,
    competencia?: string,
    tipo?: string,
    unidade?: string,
  ) => {
    const params: any = { municipio_id: municipioId };
    if (competencia) params.competencia = competencia;
    if (tipo) params.tipo = tipo;
    if (unidade) params.unidade = unidade;
    const response = await api.get('/saude/relatorios/fichas-rascunho/relatorio-pdf', {
      params,
      responseType: 'blob',
    });
    // Guard de 204: o helper de PDF dos relatorios antigos nao checa e baixaria arquivo vazio.
    if (!response.data || response.status === 204) return;
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `fichas-rascunho-${competencia || 'atual'}-${new Date().toISOString().slice(0, 10)}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  // Relatorio "Profissionais Duplicados" (cadastros de tb_prof que podem ser a mesma pessoa)
  getProfissionaisDuplicados: async (
    municipioId: number,
    opcoes: {
      modo?: 'duplicados' | 'todos';
      busca?: string;
      situacao?: string;
      unidade?: string;
      page?: number;
      limit?: number;
    } = {},
  ) => {
    const params: any = { municipio_id: municipioId };
    if (opcoes.modo) params.modo = opcoes.modo;
    if (opcoes.busca) params.busca = opcoes.busca;
    if (opcoes.situacao && opcoes.situacao !== 'todas') params.situacao = opcoes.situacao;
    if (opcoes.unidade) params.unidade = opcoes.unidade;
    if (opcoes.page) params.page = opcoes.page;
    if (opcoes.limit) params.limit = opcoes.limit;
    const response = await api.get('/saude/relatorios/profissionais-duplicados', { params });
    return response.data;
  },

  exportarProfissionaisDuplicadosCsv: async (
    municipioId: number,
    opcoes: { modo?: 'duplicados' | 'todos'; busca?: string; situacao?: string; unidade?: string } = {},
  ) => {
    const params: any = { municipio_id: municipioId };
    if (opcoes.modo) params.modo = opcoes.modo;
    if (opcoes.busca) params.busca = opcoes.busca;
    if (opcoes.situacao && opcoes.situacao !== 'todas') params.situacao = opcoes.situacao;
    if (opcoes.unidade) params.unidade = opcoes.unidade;
    const response = await api.get('/saude/relatorios/profissionais-duplicados/exportar-csv', {
      params,
      responseType: 'blob',
    });
    if (!response.data || response.status === 204) return;
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `profissionais-duplicados-${opcoes.modo || 'duplicados'}-${new Date().toISOString().slice(0, 10)}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  gerarProfissionaisDuplicadosPdf: async (
    municipioId: number,
    opcoes: { modo?: 'duplicados' | 'todos'; busca?: string; situacao?: string; unidade?: string } = {},
  ) => {
    const params: any = { municipio_id: municipioId };
    if (opcoes.modo) params.modo = opcoes.modo;
    if (opcoes.busca) params.busca = opcoes.busca;
    if (opcoes.situacao && opcoes.situacao !== 'todas') params.situacao = opcoes.situacao;
    if (opcoes.unidade) params.unidade = opcoes.unidade;
    const response = await api.get('/saude/relatorios/profissionais-duplicados/relatorio-pdf', {
      params,
      responseType: 'blob',
    });
    // Guard de 204: sem ele o navegador baixaria um arquivo vazio.
    if (!response.data || response.status === 204) return;
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `profissionais-duplicados-${opcoes.modo || 'duplicados'}-${new Date().toISOString().slice(0, 10)}.pdf`,
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  // Conexões
  getConexoes: async (municipioId: number) => {
    const response = await api.get('/saude/conexoes', {
      params: { municipio_id: municipioId },
    });
    return response.data;
  },

  createConexao: async (data: {
    municipio_id: number;
    nome: string;
    host: string;
    porta: number;
    banco: string;
    usuario: string;
    senha: string;
    ssl: boolean;
    usa_dwh?: boolean;
    usa_snapshot?: boolean;
    modo_pec?: 'direto' | 'xml';
  }) => {
    const response = await api.post('/saude/conexoes', data);
    return response.data;
  },

  updateConexao: async (id: number, data: any) => {
    const response = await api.put(`/saude/conexoes/${id}`, data);
    return response.data;
  },

  deleteConexao: async (id: number) => {
    const response = await api.delete(`/saude/conexoes/${id}`);
    return response.data;
  },

  testarConexao: async (id: number) => {
    const response = await api.post(`/saude/conexoes/${id}/testar`);
    return response.data;
  },

  // Cache
  // Snapshot D-1: estado da consolidacao noturna (alimenta o FonteDadosSelector).
  getSnapshotStatus: async (municipioId: number) => {
    const response = await api.get('/saude/snapshot/status', {
      params: { municipio_id: municipioId },
    });
    return response.data;
  },

  // Dispara a consolidacao agora. Roda a FILA INTEIRA (todos os municipios elegiveis), nao um
  // municipio avulso — mesmo desenho do cron, para nao haver duas consolidacoes concorrentes.
  executarSnapshot: async (municipioId: number) => {
    const response = await api.post('/saude/snapshot/executar', {}, {
      params: { municipio_id: municipioId },
    });
    return response.data;
  },

  getCacheStatus: async (municipioId: number) => {
    const response = await api.get('/saude/cache/status', {
      params: { municipio_id: municipioId },
    });
    return response.data;
  },

  invalidarCache: async (data: { municipio_id?: number; eixo?: string }) => {
    const response = await api.post('/saude/cache/invalidar', data);
    return response.data;
  },

  // Qualidade de Dados
  getQualidadeResumo: async (municipioId: number) => {
    const response = await api.get('/saude/qualidade-dados/resumo', { params: { municipio_id: municipioId } });
    return response.data;
  },
  getQualidadeEquipes: async (municipioId: number) => {
    const response = await api.get('/saude/qualidade-dados/equipes', { params: { municipio_id: municipioId } });
    return response.data;
  },
  getQualidadeDuplicados: async (municipioId: number, tipo = 'cpf', page = 1, limit = 50, ine?: string) => {
    const params: any = { municipio_id: municipioId, tipo, page, limit };
    if (ine) params.ine = ine;
    const response = await api.get('/saude/qualidade-dados/duplicados', { params });
    return response.data;
  },
  getQualidadeDesvinculados: async (municipioId: number, page = 1, limit = 50) => {
    const response = await api.get('/saude/qualidade-dados/desvinculados', {
      params: { municipio_id: municipioId, page, limit },
    });
    return response.data;
  },
  getQualidadeSemAcs: async (municipioId: number, page = 1, limit = 50, ine?: string) => {
    const params: any = { municipio_id: municipioId, page, limit };
    if (ine) params.ine = ine;
    const response = await api.get('/saude/qualidade-dados/sem-acs', { params });
    return response.data;
  },
  getQualidadeSemVisita: async (municipioId: number, meses = 5, page = 1, limit = 50, ine?: string) => {
    const params: any = { municipio_id: municipioId, meses, page, limit };
    if (ine) params.ine = ine;
    const response = await api.get('/saude/qualidade-dados/sem-visita', { params });
    return response.data;
  },
  getQualidadeSemCpf: async (municipioId: number, page = 1, limit = 50, ine?: string) => {
    const params: any = { municipio_id: municipioId, page, limit };
    if (ine) params.ine = ine;
    const response = await api.get('/saude/qualidade-dados/sem-cpf', { params });
    return response.data;
  },
  getQualidadeProfInvalidos: async (municipioId: number, page = 1, limit = 50, ine?: string) => {
    const params: any = { municipio_id: municipioId, page, limit };
    if (ine) params.ine = ine;
    const response = await api.get('/saude/qualidade-dados/profissionais-invalidos', { params });
    return response.data;
  },
  getQualidadeInativos: async (municipioId: number, page = 1, limit = 50, ine?: string) => {
    const params: any = { municipio_id: municipioId, page, limit };
    if (ine) params.ine = ine;
    const response = await api.get('/saude/qualidade-dados/inativos-com-atendimento', { params });
    return response.data;
  },
  exportarQualidadeCsv: async (municipioId: number, tipo: string) => {
    const response = await api.get('/saude/qualidade-dados/exportar-csv', {
      params: { municipio_id: municipioId, tipo },
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `qualidade-dados-${tipo}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  // Dados Sociodemograficos
  getDadosSocioResumo: async (municipioId: number, ine?: string) => {
    const params: any = { municipio_id: municipioId };
    if (ine) params.ine = ine;
    const response = await api.get('/saude/dados-sociodemograficos/resumo', { params });
    return response.data;
  },
  getDadosSocioEquipes: async (municipioId: number) => {
    const response = await api.get('/saude/dados-sociodemograficos/equipes', {
      params: { municipio_id: municipioId },
    });
    return response.data;
  },
  getDadosSocioNominal: async (municipioId: number, page = 1, limit = 50, ine?: string) => {
    const params: any = { municipio_id: municipioId, page, limit };
    if (ine) params.ine = ine;
    const response = await api.get('/saude/dados-sociodemograficos/nominal', { params });
    return response.data;
  },
  exportarDadosSocioCsv: async (municipioId: number, ine?: string) => {
    const params: any = { municipio_id: municipioId };
    if (ine) params.ine = ine;
    const response = await api.get('/saude/dados-sociodemograficos/exportar-csv', {
      params,
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'dados-sociodemograficos.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
};

// Servicos de configuracoes
export const settingsService = {
  getBranding: async () => {
    const response = await api.get('/configuracoes/branding');
    return response.data;
  },

  getAll: async (grupo?: string) => {
    const params = grupo ? { grupo } : {};
    const response = await api.get('/configuracoes', { params });
    return response.data;
  },

  update: async (configuracoes: { chave: string; valor: string }[]) => {
    const response = await api.patch('/configuracoes', { configuracoes });
    return response.data;
  },

  testSmtp: async (email_destino: string) => {
    const response = await api.post('/configuracoes/smtp/testar', { email_destino });
    return response.data;
  },

  testIA: async (provider: string, api_key: string, model: string) => {
    const response = await api.post('/configuracoes/ia/testar', { provider, api_key, model });
    return response.data;
  },

  getIAUsage: async (dias: number = 7) => {
    const response = await api.get('/configuracoes/ia/uso', { params: { dias } });
    return response.data;
  },

  testBI: async (url: string, user: string, pass: string) => {
    const response = await api.post('/bi/testar', { url, user, pass });
    return response.data;
  },

  testDatabase: async (config: { host?: string; port?: number; name?: string; user?: string; pass?: string }) => {
    const response = await api.post('/bi/testar-database', config);
    return response.data;
  },
};

// Servicos de reset de senha
export const passwordService = {
  forgotPassword: async (email: string) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  resetPassword: async (token: string, nova_senha: string) => {
    const response = await api.post('/auth/reset-password', { token, nova_senha });
    return response.data;
  },

  validateToken: async (token: string) => {
    const response = await api.get('/auth/validate-reset-token', { params: { token } });
    return response.data;
  },
};

// Servicos de Monitoramento (Paineis BI)
export const monitoramentoService = {
  // Conexoes DW
  getConexoesDw: async () => {
    const response = await api.get('/saude/conexoes-dw');
    return response.data;
  },
  createConexaoDw: async (data: any) => {
    const response = await api.post('/saude/conexoes-dw', data);
    return response.data;
  },
  updateConexaoDw: async (id: number, data: any) => {
    const response = await api.put(`/saude/conexoes-dw/${id}`, data);
    return response.data;
  },
  deleteConexaoDw: async (id: number) => {
    const response = await api.delete(`/saude/conexoes-dw/${id}`);
    return response.data;
  },
  testarConexaoDw: async (id: number) => {
    const response = await api.post(`/saude/conexoes-dw/${id}/testar`);
    return response.data;
  },

  // Paineis
  getStatus: async (municipioId: number) => {
    const response = await api.get('/monitoramento/paineis/status', { params: { municipio_id: municipioId } });
    return response.data;
  },
  getVisaoGeral: async (municipioId: number, dataInicio?: string, dataFim?: string) => {
    const response = await api.get('/monitoramento/paineis/visao-geral', {
      params: { municipio_id: municipioId, data_inicio: dataInicio, data_fim: dataFim },
    });
    return response.data;
  },
  getVisaoGeralEvolucao: async (municipioId: number) => {
    const response = await api.get('/monitoramento/paineis/visao-geral/evolucao', { params: { municipio_id: municipioId } });
    return response.data;
  },
  getLeitos: async (municipioId: number, dataInicio?: string, dataFim?: string) => {
    const response = await api.get('/monitoramento/paineis/leitos', {
      params: { municipio_id: municipioId, data_inicio: dataInicio, data_fim: dataFim },
    });
    return response.data;
  },
  getInternacoes: async (municipioId: number, dataInicio?: string, dataFim?: string) => {
    const response = await api.get('/monitoramento/paineis/internacoes', {
      params: { municipio_id: municipioId, data_inicio: dataInicio, data_fim: dataFim },
    });
    return response.data;
  },
  getTemposEspera: async (municipioId: number, dataInicio?: string, dataFim?: string) => {
    const response = await api.get('/monitoramento/paineis/tempos-espera', {
      params: { municipio_id: municipioId, data_inicio: dataInicio, data_fim: dataFim },
    });
    return response.data;
  },
  getRegulacao: async (municipioId: number, dataInicio?: string, dataFim?: string) => {
    const response = await api.get('/monitoramento/paineis/regulacao', {
      params: { municipio_id: municipioId, data_inicio: dataInicio, data_fim: dataFim },
    });
    return response.data;
  },
  getFilaEspera: async (municipioId: number) => {
    const response = await api.get('/monitoramento/paineis/fila-espera', { params: { municipio_id: municipioId } });
    return response.data;
  },
  getDispensacoes: async (municipioId: number, dataInicio?: string, dataFim?: string) => {
    const response = await api.get('/monitoramento/paineis/dispensacoes', {
      params: { municipio_id: municipioId, data_inicio: dataInicio, data_fim: dataFim },
    });
    return response.data;
  },
  getDispensacoesEstoqueUnidades: async (municipioId: number) => {
    const response = await api.get('/monitoramento/paineis/dispensacoes/estoque-unidades', {
      params: { municipio_id: municipioId },
    });
    return response.data;
  },
  getDispensacoesLotes: async (municipioId: number, codigoUnidade: number) => {
    const response = await api.get('/monitoramento/paineis/dispensacoes/lotes', {
      params: { municipio_id: municipioId, codigo_unidade: codigoUnidade },
    });
    return response.data;
  },
  getDispensacoesCoberturaRemume: async (
    municipioId: number,
    filtros?: { status?: string; componente?: string; pcdt_id?: number },
  ) => {
    const response = await api.get('/monitoramento/paineis/dispensacoes/cobertura-remume', {
      params: { municipio_id: municipioId, ...(filtros || {}) },
    });
    return response.data;
  },
  getCidDiagnosticos: async (municipioId: number, dataInicio?: string, dataFim?: string) => {
    const response = await api.get('/monitoramento/paineis/cid-diagnosticos', {
      params: { municipio_id: municipioId, data_inicio: dataInicio, data_fim: dataFim },
    });
    return response.data;
  },
  getGeolocalizacao: async (municipioId: number, dataInicio?: string, dataFim?: string, todos?: boolean) => {
    const response = await api.get('/monitoramento/paineis/geolocalizacao', {
      params: { municipio_id: municipioId, data_inicio: dataInicio, data_fim: dataFim, ...(todos ? { todos: "true" } : {}) },
    });
    return response.data;
  },
  getPacientes: async (municipioId: number) => {
    const response = await api.get('/monitoramento/paineis/pacientes', { params: { municipio_id: municipioId } });
    return response.data;
  },
  getSuspensao: async (municipioId: number, dataInicio?: string, dataFim?: string) => {
    const response = await api.get('/monitoramento/paineis/suspensao', {
      params: { municipio_id: municipioId, data_inicio: dataInicio, data_fim: dataFim },
    });
    return response.data;
  },
  getIcsaps: async (
    municipioId: number,
    dataInicio?: string,
    dataFim?: string,
    filtros?: Record<string, string | undefined>,
  ) => {
    const response = await api.get('/monitoramento/paineis/icsaps', {
      params: {
        municipio_id: municipioId,
        data_inicio: dataInicio,
        data_fim: dataFim,
        ...filtros,
      },
    });
    return response.data;
  },
  getIcsapsPacientes: async (
    municipioId: number,
    dataInicio: string,
    dataFim: string,
    cid: string,
    filtros?: Record<string, string | undefined>,
  ) => {
    const response = await api.get('/monitoramento/paineis/icsaps/pacientes', {
      params: {
        municipio_id: municipioId,
        data_inicio: dataInicio,
        data_fim: dataFim,
        cid,
        ...filtros,
      },
    });
    return response.data;
  },
  getVacinacao: async (
    municipioId: number,
    dataInicio?: string,
    dataFim?: string,
    filtros?: Record<string, string | undefined>,
  ) => {
    const response = await api.get('/monitoramento/paineis/vacinacao', {
      params: {
        municipio_id: municipioId,
        data_inicio: dataInicio,
        data_fim: dataFim,
        ...filtros,
      },
    });
    return response.data;
  },
  getDcnt: async (
    municipioId: number,
    dataInicio?: string,
    dataFim?: string,
    filtros?: Record<string, string | undefined>,
  ) => {
    const response = await api.get('/monitoramento/paineis/dcnt', {
      params: {
        municipio_id: municipioId,
        data_inicio: dataInicio,
        data_fim: dataFim,
        ...filtros,
      },
    });
    return response.data;
  },
  getDcntCsvUrl: (municipioId: number, filters?: Record<string, string>) => {
    const params = new URLSearchParams({
      municipio_id: String(municipioId),
      ...(filters || {}),
    });
    return '/monitoramento/paineis/dcnt/exportar-csv?' + params.toString();
  },
  getParametros: async (municipioId: number, dataInicio?: string, dataFim?: string) => {
    const response = await api.get('/monitoramento/paineis/parametros', {
      params: { municipio_id: municipioId, data_inicio: dataInicio, data_fim: dataFim },
    });
    return response.data;
  },
  getVisa: async (municipioId: number, dataInicio?: string, dataFim?: string) => {
    const response = await api.get('/monitoramento/paineis/visa', {
      params: { municipio_id: municipioId, data_inicio: dataInicio, data_fim: dataFim },
    });
    return response.data;
  },
  getMac: async (municipioId: number, dataInicio?: string, dataFim?: string) => {
    const response = await api.get('/monitoramento/paineis/mac', {
      params: { municipio_id: municipioId, data_inicio: dataInicio, data_fim: dataFim },
    });
    return response.data;
  },
  getEquipamentos: async (municipioId: number, dataInicio?: string, dataFim?: string) => {
    const response = await api.get('/monitoramento/paineis/equipamentos', {
      params: { municipio_id: municipioId, data_inicio: dataInicio, data_fim: dataFim },
    });
    return response.data;
  },
  getFpo: async (municipioId: number, dataInicio?: string, dataFim?: string) => {
    const response = await api.get('/monitoramento/paineis/fpo', {
      params: { municipio_id: municipioId, data_inicio: dataInicio, data_fim: dataFim },
    });
    return response.data;
  },
  getRepasses: async (municipioId: number, dataInicio?: string, dataFim?: string) => {
    const response = await api.get('/monitoramento/paineis/repasses', {
      params: { municipio_id: municipioId, data_inicio: dataInicio, data_fim: dataFim },
    });
    return response.data;
  },
  getUnidadesMoveis: async (municipioId: number, dataInicio?: string, dataFim?: string) => {
    const response = await api.get('/monitoramento/paineis/unidades-moveis', {
      params: { municipio_id: municipioId, data_inicio: dataInicio, data_fim: dataFim },
    });
    return response.data;
  },
  getRotaPontos: async (municipioId: number, viagemId: number) => {
    const response = await api.get("/monitoramento/paineis/unidades-moveis/rota-pontos", {
      params: { municipio_id: municipioId, viagem_id: viagemId },
    });
    return response.data;
  },

  // VISA Georreferenciamento (RF-16)
  getVisaGeoEstabelecimentos: async (municipioId: number, filters?: Record<string, string>) => {
    const response = await api.get("/monitoramento/paineis/visa-geo/estabelecimentos", {
      params: { municipio_id: municipioId, ...filters },
    });
    return response.data;
  },
  getVisaGeoHeatmap: async (municipioId: number, tipo?: string) => {
    const response = await api.get("/monitoramento/paineis/visa-geo/heatmap", {
      params: { municipio_id: municipioId, tipo },
    });
    return response.data;
  },
  getVisaGeoAlertas: async (municipioId: number) => {
    const response = await api.get("/monitoramento/paineis/visa-geo/alertas", {
      params: { municipio_id: municipioId },
    });
    return response.data;
  },
  getVisaGeoAnaliseRisco: async (municipioId: number) => {
    const response = await api.get("/monitoramento/paineis/visa-geo/analise-risco", {
      params: { municipio_id: municipioId },
    });
    return response.data;
  },
  getVisaGeoRegistroIntegrado: async (municipioId: number, estabelecimentoId: number) => {
    const response = await api.get("/monitoramento/paineis/visa-geo/registro-integrado", {
      params: { municipio_id: municipioId, id: estabelecimentoId },
    });
    return response.data;
  },
  getVisaGeoAnaliseRiscoAvancada: async (municipioId: number) => {
    const response = await api.get("/monitoramento/paineis/visa-geo/analise-risco-avancada", {
      params: { municipio_id: municipioId },
    });
    return response.data;
  },
  // VISA Monitoramento Web (RF-17)
  getVisaWebDashboard: async (municipioId: number, dataInicio?: string, dataFim?: string) => {
    const params: any = { municipio_id: municipioId };
    if (dataInicio) params.data_inicio = dataInicio;
    if (dataFim) params.data_fim = dataFim;
    const response = await api.get("/monitoramento/paineis/visa-web/dashboard", { params });
    return response.data;
  },
  getVisaWebDocumentos: async (municipioId: number, filters?: Record<string, any>) => {
    const params = { municipio_id: municipioId, ...(filters || {}) };
    const response = await api.get("/monitoramento/paineis/visa-web/documentos", { params });
    return response.data;
  },
  buscarVisaWeb: async (municipioId: number, termos: string) => {
    const response = await api.get("/monitoramento/paineis/visa-web/busca", {
      params: { municipio_id: municipioId, termos },
    });
    return response.data;
  },
  getVisaWebCsvUrl: (municipioId: number, filters?: Record<string, string>) => {
    const params = new URLSearchParams({ municipio_id: String(municipioId), ...(filters || {}) });
    return "/monitoramento/paineis/visa-web/exportar-csv?" + params.toString();
  },
  getVisaWebFontesColeta: async (municipioId: number) => {
    const response = await api.get("/monitoramento/paineis/visa-web/fontes-coleta", {
      params: { municipio_id: municipioId },
    });
    return response.data;
  },
  getVisaWebColetaHistorico: async (municipioId: number, limit = 20) => {
    const response = await api.get("/monitoramento/paineis/visa-web/coleta-historico", {
      params: { municipio_id: municipioId, limit },
    });
    return response.data;
  },
  getVisaWebNotificacoes: async (municipioId: number, limit = 15) => {
    const response = await api.get("/monitoramento/paineis/visa-web/notificacoes", {
      params: { municipio_id: municipioId, limit },
    });
    return response.data;
  },
  getVisaWebAuditoria: async (municipioId: number, limit = 50) => {
    const response = await api.get("/monitoramento/paineis/visa-web/auditoria", {
      params: { municipio_id: municipioId, limit },
    });
    return response.data;
  },
  getVisaWebPdfUrl: (municipioId: number, municipioNome: string, filters?: Record<string, string>) => {
    const params = new URLSearchParams({
      municipio_id: String(municipioId),
      municipio_nome: municipioNome,
      ...(filters || {}),
    });
    return "/monitoramento/paineis/visa-web/exportar-pdf?" + params.toString();
  },
  enviarVisaWebEmail: async (
    municipioId: number,
    email: string,
    assunto?: string,
    filtros?: Record<string, any>,
  ) => {
    const response = await api.post("/monitoramento/paineis/visa-web/enviar-email", {
      email,
      assunto,
      filtros,
    }, { params: { municipio_id: municipioId } });
    return response.data;
  },

  // ============================================================
  // RF-13: Vigilancia Epidemiologica
  // ============================================================
  getVigilanciaDashboard: async (municipioId: number, dataInicio?: string, dataFim?: string) => {
    const params: any = { municipio_id: municipioId };
    if (dataInicio) params.data_inicio = dataInicio;
    if (dataFim) params.data_fim = dataFim;
    const response = await api.get("/monitoramento/paineis/vigilancia/dashboard", { params });
    return response.data;
  },
  getVigilanciaAgravos: async (municipioId: number, filters?: Record<string, any>) => {
    const params = { municipio_id: municipioId, ...(filters || {}) };
    const response = await api.get("/monitoramento/paineis/vigilancia/agravos", { params });
    return response.data;
  },
  getVigilanciaIncidenciaRegiao: async (
    municipioId: number,
    dataInicio?: string,
    dataFim?: string,
    grupoCid?: string,
  ) => {
    const params: any = { municipio_id: municipioId };
    if (dataInicio) params.data_inicio = dataInicio;
    if (dataFim) params.data_fim = dataFim;
    if (grupoCid) params.grupo_cid = grupoCid;
    const response = await api.get("/monitoramento/paineis/vigilancia/incidencia-regiao", { params });
    return response.data;
  },
  getVigilanciaSurtos: async (municipioId: number) => {
    const response = await api.get("/monitoramento/paineis/vigilancia/surtos", { params: { municipio_id: municipioId } });
    return response.data;
  },
  getVigilanciaSeriesTemporais: async (
    municipioId: number,
    grupoCid?: string,
    dataInicio?: string,
    dataFim?: string,
  ) => {
    const params: any = { municipio_id: municipioId };
    if (grupoCid) params.grupo_cid = grupoCid;
    if (dataInicio) params.data_inicio = dataInicio;
    if (dataFim) params.data_fim = dataFim;
    const response = await api.get("/monitoramento/paineis/vigilancia/series-temporais", { params });
    return response.data;
  },
  getVigilanciaGruposCid: async (municipioId: number) => {
    const response = await api.get("/monitoramento/paineis/vigilancia/grupos-cid", { params: { municipio_id: municipioId } });
    return response.data;
  },
  getVigilanciaRegioes: async (municipioId: number) => {
    const response = await api.get("/monitoramento/paineis/vigilancia/regioes", { params: { municipio_id: municipioId } });
    return response.data;
  },
  getVigilanciaHeatmap: async (municipioId: number, dataInicio?: string, dataFim?: string) => {
    const params: any = { municipio_id: municipioId };
    if (dataInicio) params.data_inicio = dataInicio;
    if (dataFim) params.data_fim = dataFim;
    const response = await api.get("/monitoramento/paineis/vigilancia/heatmap", { params });
    return response.data;
  },
  getVigilanciaCsvUrl: (municipioId: number, filters?: Record<string, string>) => {
    const params = new URLSearchParams({
      municipio_id: String(municipioId),
      ...(filters || {}),
    });
    return "/monitoramento/paineis/vigilancia/exportar-csv?" + params.toString();
  },
  getVigilanciaPdfUrl: (municipioId: number, municipioNome: string, filters?: Record<string, string>) => {
    const params = new URLSearchParams({
      municipio_id: String(municipioId),
      municipio_nome: municipioNome,
      ...(filters || {}),
    });
    return "/monitoramento/paineis/vigilancia/exportar-pdf?" + params.toString();
  },
  enviarVigilanciaEmail: async (
    municipioId: number,
    email: string,
    assunto?: string,
    filtros?: Record<string, any>,
  ) => {
    const response = await api.post("/monitoramento/paineis/vigilancia/enviar-email", {
      email,
      assunto,
      filtros,
    }, { params: { municipio_id: municipioId } });
    return response.data;
  },

  // ============================================================
  // RF-12.3 e RF-12.4: extensoes do painel de Parametros
  // ============================================================
  getParametrosAcoesAssistenciais: async (
    municipioId: number,
    dataInicio?: string,
    dataFim?: string,
    tipoAcao?: string,
    grupoCid?: string,
  ) => {
    const params: any = { municipio_id: municipioId };
    if (dataInicio) params.data_inicio = dataInicio;
    if (dataFim) params.data_fim = dataFim;
    if (tipoAcao) params.tipo_acao = tipoAcao;
    if (grupoCid) params.grupo_cid = grupoCid;
    const response = await api.get("/monitoramento/paineis/parametros/acoes-assistenciais", { params });
    return response.data;
  },
  getParametrosCicloAssistencial: async (
    municipioId: number,
    dataInicio?: string,
    dataFim?: string,
    grupoCid?: string,
  ) => {
    const params: any = { municipio_id: municipioId };
    if (dataInicio) params.data_inicio = dataInicio;
    if (dataFim) params.data_fim = dataFim;
    if (grupoCid) params.grupo_cid = grupoCid;
    const response = await api.get("/monitoramento/paineis/parametros/ciclo-assistencial", { params });
    return response.data;
  },
  getParametrosProcedimentosNivel: async (
    municipioId: number,
    nivelAtencao?: string,
    busca?: string,
    page: number = 1,
    limit: number = 20,
  ) => {
    const params: any = { municipio_id: municipioId, page, limit };
    if (nivelAtencao) params.nivel_atencao = nivelAtencao;
    if (busca) params.busca = busca;
    const response = await api.get("/monitoramento/paineis/parametros/procedimentos-nivel", { params });
    return response.data;
  },
  getParametrosResumoNiveis: async (municipioId: number) => {
    const response = await api.get("/monitoramento/paineis/parametros/resumo-niveis", { params: { municipio_id: municipioId } });
    return response.data;
  },
};

// Servicos de Alertas
export const alertasService = {
  getTipos: async (municipioId: number) => {
    const response = await api.get('/alertas/tipos', { params: { municipio_id: municipioId } });
    return response.data;
  },
  getPreferencias: async (municipioId: number) => {
    const response = await api.get('/alertas/preferencias', { params: { municipio_id: municipioId } });
    return response.data;
  },
  updatePreferencia: async (data: { alerta_tipo_id: number; municipio_id: number; habilitado?: boolean; email_habilitado?: boolean; threshold_valor?: number | null }) => {
    const response = await api.put('/alertas/preferencias', data);
    return response.data;
  },
  getHistorico: async (municipioId: number, page = 1, limit = 20, severidade?: string, status?: string) => {
    const params: any = { municipio_id: municipioId, page, limit };
    if (severidade) params.severidade = severidade;
    if (status) params.status = status;
    const response = await api.get('/alertas/historico', { params });
    return response.data;
  },
  updateStatus: async (id: number, status: string, municipioId: number) => {
    const response = await api.patch(`/alertas/historico/${id}/status`, { status, municipio_id: municipioId });
    return response.data;
  },
  getNaoLidos: async (municipioId: number) => {
    const response = await api.get('/alertas/nao-lidos', { params: { municipio_id: municipioId } });
    return response.data as { count: number };
  },
  testar: async (municipioId: number, codigoIbge: string, municipioNome: string) => {
    const response = await api.post('/alertas/testar', { municipio_id: municipioId, codigo_ibge: codigoIbge, municipio_nome: municipioNome });
    return response.data;
  },
};

// Servicos de Atencao Especializada (RF-18)
export const especializadaService = {
  getDashboard: async (municipioId: number, dataInicio?: string, dataFim?: string) => {
    const response = await api.get('/especializada/dashboard', {
      params: { municipio_id: municipioId, data_inicio: dataInicio, data_fim: dataFim },
    });
    return response.data;
  },
  getRede: async (municipioId: number, dataInicio?: string, dataFim?: string) => {
    const response = await api.get('/especializada/rede', {
      params: { municipio_id: municipioId, data_inicio: dataInicio, data_fim: dataFim },
    });
    return response.data;
  },
  getProspeccao: async (municipioId: number, dataInicio?: string, dataFim?: string) => {
    const response = await api.get('/especializada/prospeccao', {
      params: { municipio_id: municipioId, data_inicio: dataInicio, data_fim: dataFim },
    });
    return response.data;
  },
  getProducao: async (municipioId: number, dataInicio?: string, dataFim?: string, unidade?: string) => {
    const response = await api.get('/especializada/producao', {
      params: { municipio_id: municipioId, data_inicio: dataInicio, data_fim: dataFim, unidade },
    });
    return response.data;
  },
  getCustos: async (municipioId: number, dataInicio?: string, dataFim?: string) => {
    const response = await api.get('/especializada/custos', {
      params: { municipio_id: municipioId, data_inicio: dataInicio, data_fim: dataFim },
    });
    return response.data;
  },
  getPredicao: async (municipioId: number, especialidade?: string) => {
    const response = await api.get('/especializada/predicao', {
      params: { municipio_id: municipioId, especialidade },
    });
    return response.data;
  },
  getPlanejamento: async (municipioId: number, dataInicio?: string, dataFim?: string) => {
    const response = await api.get('/especializada/planejamento', {
      params: { municipio_id: municipioId, data_inicio: dataInicio, data_fim: dataFim },
    });
    return response.data;
  },
};


// Servicos IEGM (Gestao Municipal)
export const iegmService = {
  getMunicipio: async (municipioId: number) => {
    const response = await api.get('/iegm/municipio', { params: { municipio_id: municipioId } });
    return response.data;
  },
  getRanking: async (municipioId: number) => {
    const response = await api.get('/iegm/ranking', { params: { municipio_id: municipioId } });
    return response.data;
  },
  comparar: async (municipioId: number, codigos: string[]) => {
    const response = await api.get('/iegm/comparar', { params: { municipio_id: municipioId, codigos: codigos.join(',') } });
    return response.data;
  },
  getMediaEstadual: async (municipioId: number) => {
    const response = await api.get('/iegm/media-estadual', { params: { municipio_id: municipioId } });
    return response.data;
  },
  getIndicadores: async (municipioId: number) => {
    const response = await api.get('/iegm/indicadores', { params: { municipio_id: municipioId } });
    return response.data;
  },
  getISaudeComplementar: async (municipioId: number) => {
    const response = await api.get('/iegm/isaude/complementar', { params: { municipio_id: municipioId } });
    return response.data;
  },
};

// Business Intelligence — cubo interativo (drag & drop) sobre o banco analitico do BI
// (bi_db_*), multi-fonte (atendimentos/vacinacao/dispensacao), consulta direta + RLS por municipio.
export const biService = {
  getDimensoes: async (fonte?: string) => {
    const response = await api.get('/bi/cubo/dimensoes', { params: fonte ? { fonte } : {} });
    return response.data as {
      fontes: { key: string; label: string }[];
      fonte: string;
      dimensoes: { key: string; label: string }[];
      metricas: { key: string; label: string }[];
    };
  },
  getCubo: async (payload: { municipio_id: number; fonte: string; rows: string[]; columns: string[]; metric: string }) => {
    const response = await api.post('/bi/cubo', payload);
    return response.data as {
      rowDims: { key: string; label: string; column: string }[];
      colDims: { key: string; label: string; column: string }[];
      metric: { key: string; label: string };
      data: any[];
    };
  },
  // URL de SSO para abrir o Superset completo (autoria) ja logado, restrito ao municipio.
  getEditorUrl: async (municipioId: number, redirect?: string) => {
    const response = await api.get('/bi/editor-url', {
      params: { municipio_id: municipioId, ...(redirect ? { redirect } : {}) },
    });
    return response.data as { url: string };
  },
};
